/**
 * Career Path Simulator — careers, decision banks, side hustles, key terms.
 *
 * All numbers come from the product spec. Daily gross is computed:
 *   - salary:   annual / 365
 *   - hourly:   rate * weekly_hours * 52 / 365
 *   - contract: rate * billable_hours * 52 / 365
 */

export type CompType = "salary" | "hourly" | "base_plus_commission" | "contract";

export interface CareerOption {
  key: string;
  title: string;
  compType: CompType;
  compLabel: string;
  annualIncome: number;
  hourlyRate?: number;
  weeklyHours?: number;
  dailyGross: number;
  summary: string;
  pros: string[];
  cons: string[];
  /** 0-100 baseline across the 9-dimension fit axes */
  baselineFit: CareerFitScore;
}

export interface CareerFitScore {
  stability: number;
  income_upside: number;
  flexibility: number;
  startup_cost: number; // higher = more upfront cost required
  risk: number;
  growth_potential: number;
  time_demand: number;
  skill_investment: number;
  volatility: number;
}

export const FIT_LABELS: Record<keyof CareerFitScore, string> = {
  stability: "Stability",
  income_upside: "Income upside",
  flexibility: "Flexibility",
  startup_cost: "Startup cost",
  risk: "Risk",
  growth_potential: "Growth potential",
  time_demand: "Time demand",
  skill_investment: "Skill investment",
  volatility: "Volatility",
};

export const CAREERS: CareerOption[] = [
  {
    key: "corporate_finance",
    title: "Corporate Finance",
    compType: "salary",
    compLabel: "Salary",
    annualIncome: 72000,
    dailyGross: 197.26,
    summary: "Structured company role with predictable pay and benefits.",
    pros: ["Steady salary + benefits", "Clear promotion ladder", "Strong baseline stability"],
    cons: [
      "Lower upside than commission or entrepreneurship",
      "Credential & certification expectations",
      "Promotion competition; office hours can be long",
      "Automation pressure if you never improve",
    ],
    baselineFit: {
      stability: 80, income_upside: 45, flexibility: 30, startup_cost: 10,
      risk: 25, growth_potential: 55, time_demand: 70, skill_investment: 60, volatility: 20,
    },
  },
  {
    key: "tech_sales",
    title: "Tech Sales",
    compType: "base_plus_commission",
    compLabel: "Base + commission",
    annualIncome: 65000,
    dailyGross: 178.08,
    summary: "Base salary plus commission unlocked through performance after the module.",
    pros: ["High income ceiling", "Skills compound across roles", "Commission unlocks after activation"],
    cons: ["Quota pressure & volatility", "Commission isn't guaranteed", "Stress in slow quarters"],
    baselineFit: {
      stability: 50, income_upside: 85, flexibility: 45, startup_cost: 15,
      risk: 55, growth_potential: 80, time_demand: 75, skill_investment: 65, volatility: 70,
    },
  },
  {
    key: "freelance_design",
    title: "Freelance Design",
    compType: "contract",
    compLabel: "Hourly / contract",
    annualIncome: 54600,
    hourlyRate: 35,
    weeklyHours: 30,
    dailyGross: 149.59,
    summary: "$35/hr, ~30 billable hrs/week. Income varies after activation.",
    pros: ["Full schedule autonomy", "Pricing power scales with portfolio", "No corporate politics"],
    cons: ["No employer benefits", "Income depends on clients", "Self-employment taxes apply"],
    baselineFit: {
      stability: 30, income_upside: 65, flexibility: 90, startup_cost: 40,
      risk: 65, growth_potential: 70, time_demand: 60, skill_investment: 75, volatility: 80,
    },
  },
  {
    key: "skilled_trade",
    title: "Skilled Trade",
    compType: "hourly",
    compLabel: "Hourly",
    annualIncome: 60320,
    hourlyRate: 29,
    weeklyHours: 40,
    dailyGross: 165.26,
    summary: "$29/hr, ~40 hrs/week. Overtime unlocks through events after activation.",
    pros: ["High demand, low automation risk", "Union benefits possible", "Overtime grows take-home"],
    cons: ["Physical wear, injury risk", "Tools & certs cost money upfront", "Energy demand caps side work"],
    baselineFit: {
      stability: 75, income_upside: 60, flexibility: 35, startup_cost: 55,
      risk: 50, growth_potential: 55, time_demand: 80, skill_investment: 65, volatility: 35,
    },
  },
];

/* ───── Career decision bank (one Q per career) ───── */

export type FitDelta = Partial<CareerFitScore>;

export interface DecisionChoice {
  key: string;
  label: string;
  effects: string[];
  /** Startup cost deducted from checking only on quiz pass. */
  startupCost?: number;
  fitDelta: FitDelta;
}

export interface CareerDecision {
  careerKey: string;
  prompt: string;
  choices: DecisionChoice[];
}

export const CAREER_DECISIONS: Record<string, CareerDecision> = {
  corporate_finance: {
    careerKey: "corporate_finance",
    prompt:
      "You picked Corporate Finance. Your first major decision is how to grow in a structured company. What do you do?",
    choices: [
      { key: "safe", label: "Take the safe route and only do assigned work.",
        effects: ["Stable short-term income", "Promotion score drops", "Automation risk rises"],
        fitDelta: { growth_potential: -15, stability: 10, income_upside: -20 } },
      { key: "cert", label: "Spend $600 on a finance certification.",
        effects: ["−$600 from checking on pass", "Promotion potential rises", "Stronger raise trajectory"],
        startupCost: 600,
        fitDelta: { growth_potential: 25, stability: 10, skill_investment: 20 } },
      { key: "automation", label: "Learn automation and reporting tools after work.",
        effects: ["No cash cost", "Less flexibility this year", "Stronger long-term upside"],
        fitDelta: { growth_potential: 20, flexibility: -10, income_upside: 15, skill_investment: 15 } },
      { key: "jobhop", label: "Job-hop for a higher salary without emergency savings.",
        effects: ["Possible salary boost", "Higher layoff risk", "Stability drops"],
        fitDelta: { growth_potential: 10, stability: -25, risk: 30, volatility: 20 } },
    ],
  },
  tech_sales: {
    careerKey: "tech_sales",
    prompt:
      "You picked Tech Sales. Your income can grow fast, but commission is not guaranteed. How do you approach your first quarter?",
    choices: [
      { key: "biggest_deals", label: "Chase only the biggest deals.",
        effects: ["High upside if you land them", "High chance of missing quota", "Commission volatility up"],
        fitDelta: { income_upside: 30, stability: -25, risk: 25, volatility: 25 } },
      { key: "balanced", label: "Build a balanced pipeline of small and medium deals.",
        effects: ["More consistent commission", "Strong long-term sales foundation"],
        fitDelta: { stability: 15, income_upside: 15, risk: -10, volatility: -10 } },
      { key: "training", label: "Spend $400 on sales training and prospecting tools.",
        effects: ["−$400 from checking on pass", "Better quota performance", "Higher commission potential"],
        startupCost: 400,
        fitDelta: { income_upside: 25, growth_potential: 20, skill_investment: 20 } },
      { key: "base_only", label: "Assume base salary is enough and ignore commission skills.",
        effects: ["Base pay stays flat", "Commission potential drops", "Layoff risk if quotas matter"],
        fitDelta: { stability: -10, income_upside: -30, risk: 20 } },
    ],
  },
  freelance_design: {
    careerKey: "freelance_design",
    prompt:
      "You picked Freelance Design. Your income depends on clients. What is your first move?",
    choices: [
      { key: "low_prices", label: "Charge low prices to get clients fast.",
        effects: ["Clients ramp quickly", "Burnout risk rises", "Hard to raise prices later"],
        fitDelta: { income_upside: -20, volatility: 20, time_demand: 20 } },
      { key: "niche", label: "Pick a niche and build a premium portfolio.",
        effects: ["Slower start", "Stronger pricing power", "Better long-term client quality"],
        fitDelta: { income_upside: 30, growth_potential: 20, skill_investment: 15 } },
      { key: "buy_tools", label: "Buy $500 in design software/templates immediately.",
        effects: ["−$500 from checking on pass", "Better delivery quality", "Cash pressure rises early"],
        startupCost: 500,
        fitDelta: { skill_investment: 20, risk: 15, startup_cost: 25 } },
      { key: "wait", label: "Wait until clients appear before building systems.",
        effects: ["No upfront cost", "Slow client acquisition", "Missed opportunities"],
        fitDelta: { income_upside: -20, growth_potential: -15 } },
    ],
  },
  skilled_trade: {
    careerKey: "skilled_trade",
    prompt:
      "You picked Skilled Trade. You can earn strong hourly income, but the path is physical and skill-based. What do you prioritize first?",
    choices: [
      { key: "buy_tools", label: "Buy your own tools for $700.",
        effects: ["−$700 from checking on pass", "Higher job readiness", "Overtime eligibility later"],
        startupCost: 700,
        fitDelta: { growth_potential: 25, startup_cost: 30, skill_investment: 15 } },
      { key: "overtime", label: "Take every overtime shift available.",
        effects: ["Higher daily pay", "Lower energy", "Injury/burnout risk up"],
        fitDelta: { income_upside: 20, time_demand: 25, risk: 20 } },
      { key: "apprenticeship", label: "Focus on apprenticeship training and safety.",
        effects: ["Slower short-term income", "Better long-term stability", "Lower injury risk"],
        fitDelta: { growth_potential: 20, stability: 20, risk: -20, skill_investment: 20 } },
      { key: "skip_training", label: "Skip training and rely on basic labor hours.",
        effects: ["Immediate pay continues", "Future raises limited", "Replacement risk rises"],
        fitDelta: { growth_potential: -25, stability: -15, income_upside: -20 } },
    ],
  },
};

/* ───── Side hustle bank ───── */

export interface SideHustleOption {
  key: string;
  title: string;
  startupCost: number;
  monthlyCarryingCost: number;
  metricLabel: string;
  blurb: string;
}

export const SIDE_HUSTLES: SideHustleOption[] = [
  { key: "freelancing", title: "Freelancing",
    startupCost: 150, monthlyCarryingCost: 25, metricLabel: "Active clients",
    blurb: "Tools, profile, and software. Revenue depends on client acquisition." },
  { key: "content", title: "Content Creation",
    startupCost: 250, monthlyCarryingCost: 30, metricLabel: "Followers + monetization readiness",
    blurb: "Basic gear & software. Followers ≠ money — monetization takes audience trust." },
  { key: "passive_investing", title: "Passive Investing",
    startupCost: 500, monthlyCarryingCost: 0, metricLabel: "Invested balance",
    blurb: "Minimum $500 capital. Returns are slow and variable. Requires patience." },
  { key: "none", title: "Skip side hustle for now",
    startupCost: 0, monthlyCarryingCost: 0, metricLabel: "—",
    blurb: "Focus on the core career. You can revisit side hustles later." },
];

export const SIDE_HUSTLE_DECISIONS: Record<string, CareerDecision> = {
  freelancing: {
    careerKey: "freelancing",
    prompt: "You want to start freelancing on the side. What is your launch plan?",
    choices: [
      { key: "setup", label: "Pay $150 to set up tools, portfolio, and a client profile.",
        effects: ["−$150 from checking on pass", "Active client chance rises", "Side hustle launches properly"],
        startupCost: 150,
        fitDelta: { growth_potential: 15, skill_investment: 15 } },
      { key: "no_tools", label: "Start with no tools and message random people.",
        effects: ["No startup cost", "Low client trust", "Slow revenue"],
        fitDelta: { growth_potential: -10, time_demand: 20 } },
      { key: "one_offer", label: "Offer one clear service to one specific customer type.",
        effects: ["Better conversion", "Slower but stronger growth", "Client quality improves"],
        fitDelta: { growth_potential: 15, income_upside: 20 } },
      { key: "everything", label: "Offer every service possible to everyone.",
        effects: ["Confusing offer", "Lower trust", "Higher burnout"],
        fitDelta: { growth_potential: -15, time_demand: 25 } },
    ],
  },
  content: {
    careerKey: "content",
    prompt:
      "You want to become a content creator. Followers do not equal money. What do you focus on first?",
    choices: [
      { key: "gear", label: "Buy $250 of basic gear/software and build a consistent content system.",
        effects: ["−$250 from checking on pass", "Content consistency improves", "Growth more predictable"],
        startupCost: 250,
        fitDelta: { skill_investment: 20, growth_potential: 20 } },
      { key: "viral", label: "Chase viral trends with no clear audience.",
        effects: ["Follower spikes possible", "Low trust", "Monetization readiness stays low"],
        fitDelta: { volatility: 25, growth_potential: -10 } },
      { key: "niche", label: "Build a focused niche and collect emails/leads.",
        effects: ["Slower follower growth", "Stronger monetization path", "Audience trust builds"],
        fitDelta: { growth_potential: 30, income_upside: 15 } },
      { key: "fake", label: "Buy fake followers.",
        effects: ["Follower count rises", "Engagement collapses", "Brand trust destroyed"],
        fitDelta: { growth_potential: -40, risk: 30 } },
    ],
  },
  passive_investing: {
    careerKey: "passive_investing",
    prompt: "You want passive income. What do you do first?",
    choices: [
      { key: "diversified", label: "Invest $500 into a diversified long-term portfolio.",
        effects: ["−$500 from checking on pass", "Investment account starts", "Returns are slow and variable"],
        startupCost: 500,
        fitDelta: { growth_potential: 20, stability: 10 } },
      { key: "risky", label: "Put all available money into a risky trend.",
        effects: ["Possible short-term gain", "High loss risk", "Stability drops"],
        fitDelta: { risk: 50, stability: -30, volatility: 40 } },
      { key: "wait", label: "Wait and build emergency savings before investing.",
        effects: ["No immediate investment income", "Stronger safety net", "Better long-term readiness"],
        fitDelta: { stability: 30, risk: -20 } },
      { key: "replace_job", label: "Assume passive income will replace a job quickly.",
        effects: ["Unrealistic expectation", "Poor planning score", "Future cash-flow stress"],
        fitDelta: { risk: 25, growth_potential: -30 } },
    ],
  },
};

/** Apply a fit delta on top of the career baseline, clamped to 0-100. */
export function computeFit(baseline: CareerFitScore, ...deltas: FitDelta[]): CareerFitScore {
  const out: CareerFitScore = { ...baseline };
  for (const d of deltas) {
    for (const k of Object.keys(d) as (keyof CareerFitScore)[]) {
      out[k] = Math.max(0, Math.min(100, out[k] + (d[k] || 0)));
    }
  }
  return out;
}

/** Initial side hustle metrics seeded after activation. */
export function initialSideHustleMetrics(hustleKey: string): Record<string, number | string> {
  switch (hustleKey) {
    case "freelancing":
      return { type: "freelancing", clients: 0, monthly_carrying_cost: 25 };
    case "content":
      return { type: "content", followers: 0, engagement_rate: 0, monetization_readiness: 0, monthly_carrying_cost: 30 };
    case "passive_investing":
      return { type: "passive_investing", invested_balance: 500, monthly_carrying_cost: 0 };
    default:
      return { type: "none" };
  }
}

/* ───── In-module Key Terms (drawer; no navigation) ───── */

export interface KeyTerm { term: string; definition: string }

export const CAREER_KEY_TERMS: KeyTerm[] = [
  { term: "Gross pay", definition: "Total earnings before any taxes or deductions are taken out." },
  { term: "Net pay", definition: "What lands in your account after taxes, payroll, benefits, and retirement deductions." },
  { term: "Federal income tax", definition: "Tax withheld for the federal government — funds national programs." },
  { term: "State income tax", definition: "Tax withheld for your state. Some states have none." },
  { term: "Social Security (FICA)", definition: "6.2% payroll tax that funds the federal retirement & disability system." },
  { term: "Medicare", definition: "1.45% payroll tax that funds federal health coverage for seniors." },
  { term: "Benefits / insurance", definition: "Employer health coverage, dental, vision, etc. — typically pre-tax." },
  { term: "Retirement contribution", definition: "Money set aside in a 401(k) or similar account before it hits checking." },
  { term: "Hourly wage", definition: "Pay per hour worked. Total pay depends on hours and consistency." },
  { term: "Salary", definition: "Annual pay agreed up front. Daily check = annual ÷ 365." },
  { term: "Commission", definition: "Pay tied to sales results. Volatile — unlocks after the module is activated." },
  { term: "Contract / freelance", definition: "Paid by the hour or project. No employer benefits; variable income." },
  { term: "Startup cost", definition: "Money you spend up front to launch a career path or side hustle." },
  { term: "Carrying cost", definition: "Ongoing monthly cost to keep a side hustle alive." },
  { term: "Monetization readiness", definition: "How ready an audience is to actually pay you for something." },
  { term: "Career-fit score", definition: "How your choices map across stability, upside, flexibility, risk, growth, etc." },
  { term: "Educational estimate", definition: "Simplified numbers for learning — not tax or financial advice." },
];
